import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { Icon } from '../../components/icons/Icon'
import { StatusPill } from '../../components/StatusPill'
import { InlineAlert } from '../../components/InlineAlert'
import { ApiError } from '../../api/problem'
import {
  createResidencyCertification,
  listResidencyCertifications,
  updateChainOfTitleVerified,
  updateLoanOutVerified,
  updateResidencyCertificationVerified,
  updateVendorQualified,
} from '../../api/film'
import type {
  ChainOfTitleView,
  CreateChainOfTitleRequest,
  CreateLoanOutRequest,
  CreatePersonnelRequest,
  CreateVendorRequest,
  LoanOutView,
  PersonnelView,
  ResidencyCertificationView,
  VendorView,
} from '../../types/film'

/**
 * IB-155/IB-53b Slice B — the four entity surfaces (Personnel/LoanOut/Vendor/ChainOfTitle) that
 * mount on `ProductionDetailScreen` alongside the existing Phases/FY-rollup/SpendGrid sections
 * (design §3): list + create against the already-merged CRUD backend, `verified`/`qualified`
 * ordinary flips (no `MandatoryCommentModal` — none of these fields is `@GuardedField`), and
 * `Personnel.resident`/`Vendor.inState` rendered strictly read-only (no edit control in the DOM).
 */

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof ApiError ? (err.problem.detail ?? err.message) : fallback
}

// ---------------------------------------------------------------------------
// Personnel (+ nested ResidencyCertification)
// ---------------------------------------------------------------------------

export interface PersonnelPanelProps {
  rows: PersonnelView[]
  canVerify: boolean
  onCreate: (request: CreatePersonnelRequest) => Promise<void>
}

export function PersonnelPanel({ rows, canVerify, onCreate }: PersonnelPanelProps) {
  const [showForm, setShowForm] = useState(false)
  const [personRef, setPersonRef] = useState('')
  const [line, setLine] = useState('above')
  const [role, setRole] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [expandedId, setExpandedId] = useState<string | null>(null)

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setSubmitting(true)
    setFormError(null)
    try {
      await onCreate({ personRef, line, role })
      setPersonRef('')
      setRole('')
      setShowForm(false)
    } catch (err) {
      setFormError(errorMessage(err, 'Could not add this person.'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div>
      <PanelHeader title="Personnel" onAddClick={() => setShowForm((v) => !v)} addLabel="Add personnel" />

      {showForm ? (
        <form
          onSubmit={(e) => void handleSubmit(e)}
          style={{ display: 'flex', gap: 'var(--iw-space-2)', flexWrap: 'wrap', margin: 'var(--iw-space-2) 0' }}
        >
          {formError ? <InlineAlert severity="error">{formError}</InlineAlert> : null}
          <input
            className="iw-input"
            placeholder="Person ref"
            value={personRef}
            onChange={(e) => setPersonRef(e.target.value)}
            required
            aria-label="Person ref"
          />
          <select className="iw-input" value={line} onChange={(e) => setLine(e.target.value)} aria-label="Line">
            <option value="above">Above-the-line</option>
            <option value="below">Below-the-line</option>
          </select>
          <input
            className="iw-input"
            placeholder="Role"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            required
            aria-label="Role"
          />
          <button type="submit" className="iw-btn iw-btn--primary iw-btn--sm" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save'}
          </button>
        </form>
      ) : null}

      {rows.length === 0 ? (
        <p style={{ color: 'var(--iw-text-muted)' }}>No personnel recorded yet for this production.</p>
      ) : (
        <table className="iw-table" aria-label="Personnel">
          <thead>
            <tr>
              <th scope="col">Person ref</th>
              <th scope="col">Line</th>
              <th scope="col">Role</th>
              <th scope="col">Resident</th>
              <th scope="col">Residency certifications</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <PersonnelRow
                key={row.id}
                row={row}
                canVerify={canVerify}
                expanded={expandedId === row.id}
                onToggle={() => setExpandedId((current) => (current === row.id ? null : row.id))}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

function PersonnelRow({
  row,
  canVerify,
  expanded,
  onToggle,
}: {
  row: PersonnelView
  canVerify: boolean
  expanded: boolean
  onToggle: () => void
}) {
  return (
    <>
      <tr>
        <td>{row.personRef}</td>
        <td>{row.line}</td>
        <td>{row.role}</td>
        <td>
          {/*
           * Personnel.resident is @GuardedField with zero write channels in the merged backend
           * (IB-155 design §4 decision 2) — always read-only; no edit control anywhere in the DOM.
           */}
          {row.resident === null ? (
            <span style={{ color: 'var(--iw-text-subtle)' }}>Not yet assessed</span>
          ) : (
            <StatusPill variant={row.resident ? 'success' : 'neutral'} label={row.resident ? 'Resident' : 'Non-resident'} />
          )}
        </td>
        <td>
          <button
            type="button"
            className="iw-btn iw-btn--sm"
            onClick={onToggle}
            aria-expanded={expanded}
            aria-label={`Residency certifications for ${row.personRef}`}
          >
            {expanded ? 'Hide' : 'Show'}
          </button>
        </td>
      </tr>
      {expanded ? (
        <tr>
          <td colSpan={5}>
            <ResidencyCertificationList personnelId={row.id} canVerify={canVerify} />
          </td>
        </tr>
      ) : null}
    </>
  )
}

function ResidencyCertificationList({ personnelId, canVerify }: { personnelId: string; canVerify: boolean }) {
  const [rows, setRows] = useState<ResidencyCertificationView[]>([])
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading')
  const [error, setError] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [stateField, setStateField] = useState('')
  const [evidenceRef, setEvidenceRef] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  const load = useCallback(() => {
    setState('loading')
    listResidencyCertifications(personnelId)
      .then((data) => {
        setRows(data)
        setState('ready')
      })
      .catch((err) => {
        setError(errorMessage(err, 'Could not load residency certifications.'))
        setState('error')
      })
  }, [personnelId])

  useEffect(() => {
    load()
  }, [load])

  async function handleVerify(cert: ResidencyCertificationView) {
    try {
      const updated = await updateResidencyCertificationVerified(personnelId, cert.id, !cert.verified, cert.version)
      setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)))
    } catch (err) {
      setError(errorMessage(err, 'Could not update this certification.'))
    }
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setSubmitting(true)
    setFormError(null)
    try {
      const created = await createResidencyCertification(personnelId, { state: stateField, evidenceRef })
      setRows((prev) => [created, ...prev])
      setStateField('')
      setEvidenceRef('')
      setShowForm(false)
    } catch (err) {
      setFormError(errorMessage(err, 'Could not add this residency certification.'))
    } finally {
      setSubmitting(false)
    }
  }

  if (state === 'loading') {
    return <p style={{ color: 'var(--iw-text-muted)' }}>Loading residency certifications…</p>
  }
  if (state === 'error') {
    return (
      <InlineAlert
        severity="error"
        action={
          <button type="button" className="iw-btn iw-btn--sm" onClick={load}>
            Retry
          </button>
        }
      >
        {error}
      </InlineAlert>
    )
  }

  return (
    <div style={{ padding: 'var(--iw-space-2) 0' }}>
      <button type="button" className="iw-btn iw-btn--sm" onClick={() => setShowForm((v) => !v)}>
        <Icon name="plus" size={12} /> Add residency certification
      </button>
      {showForm ? (
        <form
          onSubmit={(e) => void handleSubmit(e)}
          style={{ display: 'flex', gap: 'var(--iw-space-2)', marginTop: 'var(--iw-space-2)', flexWrap: 'wrap' }}
        >
          {formError ? <InlineAlert severity="error">{formError}</InlineAlert> : null}
          <input
            className="iw-input"
            placeholder="State"
            value={stateField}
            onChange={(e) => setStateField(e.target.value)}
            required
            aria-label="State"
          />
          <input
            className="iw-input"
            placeholder="Evidence reference"
            value={evidenceRef}
            onChange={(e) => setEvidenceRef(e.target.value)}
            required
            aria-label="Evidence reference"
          />
          <button type="submit" className="iw-btn iw-btn--primary iw-btn--sm" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save'}
          </button>
        </form>
      ) : null}

      {rows.length === 0 ? (
        <p style={{ color: 'var(--iw-text-muted)' }}>No residency certification on file.</p>
      ) : (
        <ul style={{ listStyle: 'none', margin: 'var(--iw-space-2) 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-2)' }}>
          {rows.map((cert) => (
            <li key={cert.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-2)' }}>
              <span>{cert.state}</span>
              <StatusPill variant={cert.verified ? 'success' : 'neutral'} label={cert.verified ? 'Verified' : 'Unverified'} />
              {canVerify ? (
                <button type="button" className="iw-btn iw-btn--sm" onClick={() => void handleVerify(cert)}>
                  {cert.verified ? 'Mark unverified' : 'Mark verified'}
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// LoanOut
// ---------------------------------------------------------------------------

export interface LoanOutPanelProps {
  rows: LoanOutView[]
  canVerify: boolean
  onCreate: (request: CreateLoanOutRequest) => Promise<void>
  onRowUpdated: (row: LoanOutView) => void
}

export function LoanOutPanel({ rows, canVerify, onCreate, onRowUpdated }: LoanOutPanelProps) {
  const [showForm, setShowForm] = useState(false)
  const [name, setName] = useState('')
  const [registrationStatus, setRegistrationStatus] = useState('')
  const [withholdingStatus, setWithholdingStatus] = useState('')
  const [jurisdiction, setJurisdiction] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [rowError, setRowError] = useState<string | null>(null)

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setSubmitting(true)
    setFormError(null)
    try {
      await onCreate({ name, registrationStatus, withholdingStatus, jurisdiction })
      setName('')
      setRegistrationStatus('')
      setWithholdingStatus('')
      setJurisdiction('')
      setShowForm(false)
    } catch (err) {
      setFormError(errorMessage(err, 'Could not add this loan-out company.'))
    } finally {
      setSubmitting(false)
    }
  }

  async function handleVerify(row: LoanOutView) {
    setRowError(null)
    try {
      const updated = await updateLoanOutVerified(row.id, !row.verified, row.version)
      onRowUpdated(updated)
    } catch (err) {
      setRowError(errorMessage(err, 'Could not update this loan-out.'))
    }
  }

  return (
    <div>
      <PanelHeader title="Loan-out companies" onAddClick={() => setShowForm((v) => !v)} addLabel="Add loan-out" />
      {rowError ? <InlineAlert severity="error">{rowError}</InlineAlert> : null}

      {showForm ? (
        <form
          onSubmit={(e) => void handleSubmit(e)}
          style={{ display: 'flex', gap: 'var(--iw-space-2)', flexWrap: 'wrap', margin: 'var(--iw-space-2) 0' }}
        >
          {formError ? <InlineAlert severity="error">{formError}</InlineAlert> : null}
          <input className="iw-input" placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} required aria-label="Name" />
          <input
            className="iw-input"
            placeholder="Registration status"
            value={registrationStatus}
            onChange={(e) => setRegistrationStatus(e.target.value)}
            required
            aria-label="Registration status"
          />
          <input
            className="iw-input"
            placeholder="Withholding status"
            value={withholdingStatus}
            onChange={(e) => setWithholdingStatus(e.target.value)}
            required
            aria-label="Withholding status"
          />
          <input
            className="iw-input"
            placeholder="Jurisdiction"
            value={jurisdiction}
            onChange={(e) => setJurisdiction(e.target.value)}
            required
            aria-label="Jurisdiction"
          />
          <button type="submit" className="iw-btn iw-btn--primary iw-btn--sm" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save'}
          </button>
        </form>
      ) : null}

      {rows.length === 0 ? (
        <p style={{ color: 'var(--iw-text-muted)' }}>No loan-out companies recorded yet.</p>
      ) : (
        <table className="iw-table" aria-label="Loan-out companies">
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">Registration</th>
              <th scope="col">Withholding</th>
              <th scope="col">Jurisdiction</th>
              <th scope="col">Verified</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>{row.name}</td>
                <td>{row.registrationStatus}</td>
                <td>{row.withholdingStatus}</td>
                <td>{row.jurisdiction}</td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-2)' }}>
                    <StatusPill variant={row.verified ? 'success' : 'neutral'} label={row.verified ? 'Verified' : 'Unverified'} />
                    {canVerify ? (
                      <button
                        type="button"
                        className="iw-btn iw-btn--sm"
                        onClick={() => void handleVerify(row)}
                        aria-label={`Toggle verified for ${row.name}`}
                      >
                        {row.verified ? 'Mark unverified' : 'Mark verified'}
                      </button>
                    ) : null}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Vendor
// ---------------------------------------------------------------------------

export interface VendorPanelProps {
  rows: VendorView[]
  onCreate: (request: CreateVendorRequest) => Promise<void>
  onRowUpdated: (row: VendorView) => void
}

export function VendorPanel({ rows, onCreate, onRowUpdated }: VendorPanelProps) {
  const [showForm, setShowForm] = useState(false)
  const [name, setName] = useState('')
  const [inState, setInState] = useState(false)
  const [qualified, setQualified] = useState(false)
  const [address, setAddress] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [rowError, setRowError] = useState<string | null>(null)

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setSubmitting(true)
    setFormError(null)
    try {
      // Both fields are required up front (AC17) -- no later edit path exists for the in-state
      // flag. ES6 shorthand property syntax (same-named local state variables) builds the request
      // directly from local form state.
      await onCreate({ name, inState, qualified, address })
      setName('')
      setInState(false)
      setQualified(false)
      setAddress('')
      setShowForm(false)
    } catch (err) {
      setFormError(errorMessage(err, 'Could not add this vendor.'))
    } finally {
      setSubmitting(false)
    }
  }

  async function handleToggleQualified(row: VendorView) {
    setRowError(null)
    try {
      const updated = await updateVendorQualified(row.id, !row.qualified, row.version)
      onRowUpdated(updated)
    } catch (err) {
      setRowError(errorMessage(err, 'Could not update this vendor.'))
    }
  }

  return (
    <div>
      <PanelHeader title="Vendors" onAddClick={() => setShowForm((v) => !v)} addLabel="Add vendor" />
      {rowError ? <InlineAlert severity="error">{rowError}</InlineAlert> : null}

      {showForm ? (
        <form
          onSubmit={(e) => void handleSubmit(e)}
          style={{ display: 'flex', gap: 'var(--iw-space-2)', flexWrap: 'wrap', margin: 'var(--iw-space-2) 0', alignItems: 'center' }}
        >
          {formError ? <InlineAlert severity="error">{formError}</InlineAlert> : null}
          <input className="iw-input" placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} required aria-label="Name" />
          <input
            className="iw-input"
            placeholder="Address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            required
            aria-label="Address"
          />
          <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <input type="checkbox" checked={inState} onChange={(e) => setInState(e.target.checked)} /> In-state
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <input type="checkbox" checked={qualified} onChange={(e) => setQualified(e.target.checked)} /> Qualified
          </label>
          <button type="submit" className="iw-btn iw-btn--primary iw-btn--sm" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save'}
          </button>
        </form>
      ) : null}

      {rows.length === 0 ? (
        <p style={{ color: 'var(--iw-text-muted)' }}>No vendors recorded yet.</p>
      ) : (
        <table className="iw-table" aria-label="Vendors">
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">Address</th>
              <th scope="col">In-state</th>
              <th scope="col">Qualified</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>{row.name}</td>
                <td>{row.address}</td>
                <td>
                  {/* Vendor.inState has no update endpoint (IB-155 design §4 decision 3) -- read-only
                      after creation; no edit control in the DOM. */}
                  <StatusPill variant={row.inState ? 'success' : 'neutral'} label={row.inState ? 'In-state' : 'Out-of-state'} />
                </td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-2)' }}>
                    <StatusPill variant={row.qualified ? 'success' : 'neutral'} label={row.qualified ? 'Qualified' : 'Not qualified'} />
                    <button
                      type="button"
                      className="iw-btn iw-btn--sm"
                      onClick={() => void handleToggleQualified(row)}
                      aria-label={`Toggle qualified for ${row.name}`}
                    >
                      {row.qualified ? 'Mark not qualified' : 'Mark qualified'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// ChainOfTitle
// ---------------------------------------------------------------------------

export interface ChainOfTitlePanelProps {
  productionId: string
  rows: ChainOfTitleView[]
  canVerify: boolean
  onCreate: (request: CreateChainOfTitleRequest) => Promise<void>
  onRowUpdated: (row: ChainOfTitleView) => void
}

export function ChainOfTitlePanel({ productionId, rows, canVerify, onCreate, onRowUpdated }: ChainOfTitlePanelProps) {
  const [showForm, setShowForm] = useState(false)
  const [evidenceRef, setEvidenceRef] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [rowError, setRowError] = useState<string | null>(null)

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setSubmitting(true)
    setFormError(null)
    try {
      await onCreate({ evidenceRef })
      setEvidenceRef('')
      setShowForm(false)
    } catch (err) {
      setFormError(errorMessage(err, 'Could not add this chain-of-title record.'))
    } finally {
      setSubmitting(false)
    }
  }

  async function handleVerify(row: ChainOfTitleView) {
    setRowError(null)
    try {
      const updated = await updateChainOfTitleVerified(productionId, row.id, !row.verified, row.version)
      onRowUpdated(updated)
    } catch (err) {
      setRowError(errorMessage(err, 'Could not update this chain-of-title record.'))
    }
  }

  return (
    <div>
      <PanelHeader title="Chain of title" onAddClick={() => setShowForm((v) => !v)} addLabel="Add chain-of-title record" />
      {rowError ? <InlineAlert severity="error">{rowError}</InlineAlert> : null}

      {showForm ? (
        <form
          onSubmit={(e) => void handleSubmit(e)}
          style={{ display: 'flex', gap: 'var(--iw-space-2)', flexWrap: 'wrap', margin: 'var(--iw-space-2) 0' }}
        >
          {formError ? <InlineAlert severity="error">{formError}</InlineAlert> : null}
          <input
            className="iw-input"
            placeholder="Evidence reference"
            value={evidenceRef}
            onChange={(e) => setEvidenceRef(e.target.value)}
            required
            aria-label="Evidence reference"
          />
          <button type="submit" className="iw-btn iw-btn--primary iw-btn--sm" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save'}
          </button>
        </form>
      ) : null}

      {rows.length === 0 ? (
        <p style={{ color: 'var(--iw-text-muted)' }}>No chain-of-title records recorded yet.</p>
      ) : (
        <table className="iw-table" aria-label="Chain of title">
          <thead>
            <tr>
              <th scope="col">Evidence reference</th>
              <th scope="col">Verified</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>{row.evidenceRef ?? '—'}</td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-2)' }}>
                    <StatusPill variant={row.verified ? 'success' : 'neutral'} label={row.verified ? 'Verified' : 'Unverified'} />
                    {canVerify ? (
                      <button
                        type="button"
                        className="iw-btn iw-btn--sm"
                        onClick={() => void handleVerify(row)}
                        aria-label={`Toggle verified for chain-of-title record ${row.id}`}
                      >
                        {row.verified ? 'Mark unverified' : 'Mark verified'}
                      </button>
                    ) : null}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Shared
// ---------------------------------------------------------------------------

function PanelHeader({ title, onAddClick, addLabel }: { title: string; onAddClick: () => void; addLabel: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <h2 style={{ margin: 0, fontSize: 'var(--iw-size-md)' }}>{title}</h2>
      <button type="button" className="iw-btn iw-btn--sm" onClick={onAddClick}>
        <Icon name="plus" size={12} /> {addLabel}
      </button>
    </div>
  )
}
